if (typeof window.Bundles === "undefined") {
  class Bundles {
    constructor() {
      // Only initialize common properties, not DOM-specific ones
      this.productId = null;
      this.shopDomain = "";
      this.productHandle = "";
      this.customerProvince = "";
      this.shopLocale = "en";
      this.productData = {};
      this.bundleData = [];
      this.token = null;
      this.routes = {
        root: window.Shopify?.routes?.root || "",
        cart: null,
        cartAdd: null,
      };
      this.eventListeners = new Set();
    }

    // Initialize common properties from DOM attributes
    initializeFromElement(container) {
      this.container = container;
      this.productId = container.getAttribute("data-product-id");
      this.shopDomain = container.getAttribute("data-shop-domain") || "";
      this.productHandle = container.getAttribute("data-product-handle") || "";
      this.customerProvince = container.getAttribute("data-customer-province") || "master";
      this.shopLocale = container.getAttribute("data-shop-locale") || "en";

      this.routes.cart = container.getAttribute("data-cart-url");
      this.routes.cartAdd = container.getAttribute("data-cart-add-url");
    }

    // Common error handling
    handleError(error, context = "Unknown") {
      console.error(`Bundle Error [${context}]:`, error);

      // Dispatch custom error event for external error tracking
      this.dispatchEvent(
        new CustomEvent("bundle-error", {
          detail: { error, context },
          bubbles: true,
        })
      );
    }

    // Common utility methods
    escapeHtml(text) {
      if (typeof text !== "string") return "";
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    }

    validateRequired(value, fieldName) {
      if (!value) {
        throw new Error(`${fieldName} is required`);
      }
      return value;
    }

    // Common API methods

    async getAllBundles() {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        // TODO: if more than 6 bundles paginate.
        const apiUrl = `https://api.fastbundle.co/v3/all-bundles/?shop=${this.shopDomain}&lang=${this.shopLocale}&page=1&is_preview=false`;

        const response = await fetch(apiUrl, requestOptions);

        if (!response.ok) {
          throw new Error(`Get All bundles API failed: ${response.status}`);
        }

        const result = await response.json();
        console.log("Get All bundles result:", result);
        return result.results;
      } catch (error) {
        this.handleError(error, "getAllBundles");
        return null;
      }
    }

    async getBundleAsProduct() {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        const apiUrl = `https://api.fastbundle.co/v3/baps/${this.productId}/?shop=${this.shopDomain}&lang=${this.shopLocale}&is_preview=false&url=/products/${this.productHandle}`;

        const response = await fetch(apiUrl, requestOptions);

        if (!response.ok) {
          throw new Error(`Bundle as Product API failed: ${response.status}`);
        }

        const result = await response.json();
        return [result]; // Wrap in array for consistency and reusability of rendering logic
      } catch (error) {
        this.handleError(error, "getBundleAsProduct");
        return null;
      }
    }

    async getBundles() {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        const shop = this.validateRequired(this.shopDomain || window.Shopify?.shop, "Shop domain");
        const lang = this.shopLocale;
        const isPreview = "false";
        const url = encodeURIComponent(window.location.pathname);
        const apiUrl = `https://api.fastbundle.co/v3/product-bundles/?shop=${shop}&product_id=${this.productId}&lang=${lang}&is_preview=${isPreview}&url=${url}`;

        const response = await fetch(apiUrl, requestOptions);

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        return result;
      } catch (error) {
        this.handleError(error, "getBundles");
        return null;
      }
    }

    async getShopifyProductsData() {
      if (!Array.isArray(this.bundleData)) return {};

      const bundleEntries = await Promise.all(
        this.bundleData.map(async (bundle) => {
          const items = Array.isArray(bundle.items) ? bundle.items : [];

          const itemEntries = await Promise.all(
            items.map(async (item) => {
              const data = await this.getProductData(item);
              return [item.id, data];
            })
          );

          return [bundle.id, { items: Object.fromEntries(itemEntries) }];
        })
      );

      return Object.fromEntries(bundleEntries);
    }

    async getProductData(item) {
      try {
        const root = this.routes?.root || window.Shopify?.routes?.root || "/";
        const url = `${root}products/${item.handle}?view=restriction-checker`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`getProductData check failed: ${response.status}`);
        }

        const data = await response.json();
        const isRestricted = data.isRestricted;
        const productTags = data.productTags ? data.productTags.split(",") : [];
        const dceEquivalency = data.dceEquivalency ? data.dceEquivalency : "";
        const potencyCbdValue = data.potencyCbdValue ? data.potencyCbdValue : "";
        const potencyThcValue = data.potencyThcValue ? data.potencyThcValue : "";
        const cbdRangeMin = data.cbdRangeMin ? data.cbdRangeMin : "";
        const cbdRangeMax = data.cbdRangeMax ? data.cbdRangeMax : "";
        const thcRangeMin = data.thcRangeMin ? data.thcRangeMin : "";
        const thcRangeMax = data.thcRangeMax ? data.thcRangeMax : "";
        const potencyUom = data.potencyUom ? data.potencyUom : "";
        const cannabinoidClassification = data.cannabinoidClassification ? data.cannabinoidClassification : "";
        const isCannabisProduct = data.isCannabisProduct ? data.isCannabisProduct : "";

        const decodeHtmlEntities = (htmlString) => {
          if (typeof htmlString !== "string") return "";
          const textArea = document.createElement("textarea");
          textArea.innerHTML = htmlString;
          return textArea.value;
        };

        const encodeAttributeValue = (value) => {
          if (typeof value !== "string") return "";
          return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/'/g, "&#39;").replace(/"/g, "&quot;");
        };

        const sanitizeVariantOptionsHtml = (htmlString) => {
          if (typeof htmlString !== "string") return "";
          if (!htmlString.includes("data-properties=")) {
            return decodeHtmlEntities(htmlString);
          }

          const decoded = decodeHtmlEntities(htmlString);
          const token = "data-properties='";
          let result = "";
          let searchIndex = 0;

          while (searchIndex < decoded.length) {
            const tokenIndex = decoded.indexOf(token, searchIndex);
            if (tokenIndex === -1) {
              result += decoded.slice(searchIndex);
              break;
            }

            const valueStart = tokenIndex + token.length;
            result += decoded.slice(searchIndex, valueStart);

            let cursor = valueStart;
            let buffer = "";
            let inJsonString = false;
            let escaped = false;

            while (cursor < decoded.length) {
              const char = decoded[cursor];

              if (!escaped && char === '"') {
                inJsonString = !inJsonString;
              }

              if (!escaped && char === "\\" && inJsonString) {
                buffer += char;
                escaped = true;
                cursor += 1;
                continue;
              }

              if (!inJsonString && char === "'") {
                break;
              }

              buffer += char;
              escaped = false;
              cursor += 1;
            }

            const encodedBuffer = encodeAttributeValue(buffer);
            result += encodedBuffer;

            if (cursor < decoded.length && decoded[cursor] === "'") {
              result += "'";
              cursor += 1;
            }

            searchIndex = cursor;
          }

          return result;
        };

        const sanitizedHtml = sanitizeVariantOptionsHtml(data.variantOptionsHtml);
        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitizedHtml, "text/html");

        const container = doc.querySelector(".product__variant-options");
        const buttons = container ? container.querySelectorAll(".js--product-variant-option") : [];

        // Map each button to an object with relevant data attributes
        const parsedVariants = Array.from(buttons).map((btn) => {
          const decodePropertiesString = (value) => {
            if (typeof value !== "string") return null;
            const decodedValue = decodeHtmlEntities(value);
            try {
              return JSON.parse(decodedValue);
            } catch (error) {
              return null;
            }
          };

          let properties = decodePropertiesString(btn.getAttribute("data-properties"));

          if (properties === null) {
            properties = {};
          }
          return {
            variantId: btn.getAttribute("data-variant-id"),
            title: btn.getAttribute("data-variant-title"),
            price: btn.getAttribute("data-variant-price"),
            province: btn.textContent.trim().split("/").pop().trim(),
            properties: properties,
            onSale: btn.getAttribute("data-variant-on-sale") === "true",
            inventory: btn.getAttribute("data-variant-inventory"),
            selected: btn.classList.contains("selected"),
            tscCustomer: btn.getAttribute("data-tsc-customer"),
            tscVariant: btn.getAttribute("data-tsc-variant-option"),
          };
        });

        return {
          productId: item.id,
          productTitle: data.productTitle,
          handle: item.handle,
          parsedVariants: parsedVariants,
          isRestricted: isRestricted == "false" ? false : true,
          isBundleGift: productTags.includes("bundle-gift"),
          plantType: productTags.find((tag) => tag.includes("plant_type::")) || "",
          dceEquivalency: dceEquivalency,
          potencyCbdValue: potencyCbdValue,
          potencyThcValue: potencyThcValue,
          cbdRangeMin: cbdRangeMin,
          cbdRangeMax: cbdRangeMax,
          thcRangeMin: thcRangeMin,
          thcRangeMax: thcRangeMax,
          potencyUom: potencyUom,
          cannabinoidClassification: cannabinoidClassification,
          isCannabisProduct: isCannabisProduct == "true" ? true : false,
        };
      } catch (error) {
        if (typeof this.handleError === "function") {
          console.log("getProductData error:", error);
        } else {
          console.error("getProductData error:", error);
        }
        return {};
      }
    }

    async getProductJson(handle) {
      try {
        if (!handle) {
          throw new Error("Product handle is required");
        }
        const url = `${this.routes.root}products/${handle}.js`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Product JSON fetch failed: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        this.handleError(error, "getProductJson");
        return null;
      }
    }

    async getShopifyCart() {
      const root = this.routes?.root || window.Shopify?.routes?.root || "/";
      const cartUrl = `${root}cart.js`;
      const response = await fetch(cartUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const cartData = await response.json();
      this.token = cartData.token;
      return cartData;
    }

    // Common bundle validation methods
    async getIsBundleRestricted(bundleId) {
      if (!this.productData || !this.productData[bundleId]) return false;
      const items = this.productData[bundleId]?.items || {};
      for (const itemData of Object.values(items)) {
        if (itemData.isRestricted) {
          return true;
        }
      }
      return false;
    }

    async getIsBundleOOS(bundleId) {
      if (!this.productData || !this.productData[bundleId]) return false;
      const items = this.productData[bundleId]?.items || {};
      for (const itemData of Object.values(items)) {
        if (
          Array.isArray(itemData.parsedVariants) &&
          itemData.parsedVariants.length > 0 &&
          typeof itemData.parsedVariants[0].province === "string" &&
          itemData.parsedVariants[0].province.includes("Out of Stock")
        ) {
          return true;
        }
      }
      return false;
    }

    async getIsBundleTSCRestricted(bundleId) {
      if (!this.productData || !this.productData[bundleId]) return false;
      const items = this.productData[bundleId]?.items || {};
      for (const itemData of Object.values(items)) {
        if (Array.isArray(itemData.parsedVariants) && itemData.parsedVariants.length > 0) {
          const tscCustomerTags = itemData.parsedVariants[0].tscCustomer ? itemData.parsedVariants[0].tscCustomer.split(",") : [];
          const tscVariant = itemData.parsedVariants[0].tscVariant;
          if (tscVariant) {
            const isTagIncluded = tscCustomerTags.some((tag) => tscVariant.includes(tag));
            if (!isTagIncluded) {
              return true;
            }
          }
        }
      }
      return false;
    }

    // Common cart methods
    createAddToCartobjects(bundle) {
      let cartBundleItems = [];
      let cartShopifyItems = [];
      let totalPrice = 0;
      let totalDce = 0;
      let cbdValues = [];
      let thcValues = [];
      let cbdRangeMinValues = [];
      let cbdRangeMaxValues = [];
      let thcRangeMinValues = [];
      let thcRangeMaxValues = [];
      let potencyUomValues = [];
      let cannabinoidClassificationValues = [];
      let plantTypeValues = [];
      const bundleData = this.productData[bundle.id] || {};

      const getProvinceOptionKey = (item) => {
        const provinceOption = item.options.find((option) => option.name.toLowerCase() === "province");
        return provinceOption ? `option${provinceOption.position}` : null;
      };

      const getNotProvinceOptionKey = (item) => {
        const provinceOption = item.options.find((option) => option.name.toLowerCase() === "province");
        return provinceOption ? `option${provinceOption.position === 1 ? 2 : 1}` : null;
      };

      const getNotProvinceOption = (item) => {
        return item.options.find((option) => option.name.toLowerCase() !== "province");
      };

      const hasMultipleOptions = (item) => {
        for (const option of item.options) {
          if (option.name.toLowerCase() !== "province" && option.values.length > 1) {
            return true;
          }
        }
        return false;
      };

      const findFirstAvailableVariant = (item, customerProvince) => {
        const provinceOptionKey = getProvinceOptionKey(item);
        const notProvinceOption = getNotProvinceOption(item);
        if (!provinceOptionKey || !notProvinceOption) return null;
        for (const value of notProvinceOption.values) {
          const matchingVariant = item.variants.find(
            (variant) =>
              variant[provinceOptionKey] === customerProvince && variant[`option${notProvinceOption.position}`] === value && variant.quantity > 0
          );
          if (matchingVariant) {
            return matchingVariant;
          }
        }
        console.log("No variant available");
        return null;
      };

      const findMatchingVariant = (item, customerProvince) => {
        const provinceOptionKey = getProvinceOptionKey(item);
        if (provinceOptionKey) {
          return item.variants.find((variant) => variant[provinceOptionKey] === customerProvince);
        }
        return item.variants?.[0] || null;
      };

      const getDominant = (values) => {
        if (!values || values.length === 0) return null;

        const firstValue = values[0];
        const allValuesMatch = values.every((value) => value === firstValue);

        if (allValuesMatch && firstValue !== null && firstValue !== undefined) {
          return firstValue;
        }

        return null;
      };

      const getRequiredItems = (cartShopifyItems) => {
        return cartShopifyItems.map((item) => {
          if (item.properties._is_bundle_gift) {
            const requiredItems = cartShopifyItems
              .filter((otherItem) => otherItem.id !== item.id)
              .map((otherItem) => ({
                id: otherItem.id,
                quantity: otherItem.quantity,
              }));

            return {
              ...item,
              properties: {
                ...item.properties,
                _requiredItems: requiredItems,
              },
            };
          }
          return item;
        });
      };

      bundle.items.forEach((item) => {
        let productId = item.id;
        const productData = (bundleData.items || {})[productId];
        if (!productData) return;
        const selectedVariant = hasMultipleOptions(item)
          ? findFirstAvailableVariant(item, this.customerProvince)
          : findMatchingVariant(item, this.customerProvince);

        if (!selectedVariant) return;

        productData.selectedVariant = selectedVariant;
        productData.item = item;

        const isCannabisProduct = productData.isCannabisProduct;
        const plantType = productData.plantType;

        totalPrice += (selectedVariant.raw_price || 0) * (item.quantity || 1);
        totalDce += parseFloat(productData.dceEquivalency) || 0;

        if(isCannabisProduct) {
          if (productData.potencyCbdValue && productData.potencyCbdValue !== "") {
            cbdValues.push(parseFloat(productData.potencyCbdValue));
          }
          if (productData.potencyThcValue && productData.potencyThcValue !== "") {
            thcValues.push(parseFloat(productData.potencyThcValue));
          }
          if (productData.cbdRangeMin && productData.cbdRangeMin !== "") {
            cbdRangeMinValues.push(parseFloat(productData.cbdRangeMin));
          }
          if (productData.cbdRangeMax && productData.cbdRangeMax !== "") {
            cbdRangeMaxValues.push(parseFloat(productData.cbdRangeMax));
          }
          if (productData.thcRangeMin && productData.thcRangeMin !== "") {
            thcRangeMinValues.push(parseFloat(productData.thcRangeMin));
          }
          if (productData.thcRangeMax && productData.thcRangeMax !== "") {
            thcRangeMaxValues.push(parseFloat(productData.thcRangeMax));
          }
          if (productData.potencyUom && productData.potencyUom !== "") {
            potencyUomValues.push(productData.potencyUom);
          }
          if (productData.cannabinoidClassification && productData.cannabinoidClassification !== "") {
            cannabinoidClassificationValues.push(productData.cannabinoidClassification);
          }
        }
        
        if (plantType) {
          plantTypeValues.push(plantType);
        }

        cartBundleItems.push({
          variant_id: selectedVariant.id,
          item_id: item.item_id,
        });

        const itemProperties = productData?.parsedVariants?.find((variant) => parseInt(variant.variantId) === selectedVariant.id);

        if (productData?.isBundleGift && itemProperties && itemProperties.properties) {
          itemProperties.properties["_is_bundle_gift"] = true;
        }

        cartShopifyItems.push({
          id: selectedVariant.id,
          quantity: item.quantity,
          properties: itemProperties && itemProperties.properties ? itemProperties.properties : {},
        });
      });

      const cartShopifyItemsWithRequiredItems = getRequiredItems(cartShopifyItems);

      // console.log("cbdValues", cbdValues);
      // console.log("thcValues", thcValues);
      // console.log("cbdRangeMinValues", cbdRangeMinValues);
      // console.log("cbdRangeMaxValues", cbdRangeMaxValues);
      // console.log("thcRangeMinValues", thcRangeMinValues);
      // console.log("thcRangeMaxValues", thcRangeMaxValues);
      // console.log("cannabinoidClassificationValues", cannabinoidClassificationValues);
      // console.log("plantTypeValues", plantTypeValues);

      const averageCbd = cbdValues.length > 0 ? Math.ceil(cbdValues.reduce((a, b) => a + b, 0) / cbdValues.length) : 0;
      const averageThc = thcValues.length > 0 ? Math.ceil(thcValues.reduce((a, b) => a + b, 0) / thcValues.length) : 0;
      const cbdRangeMin = cbdRangeMinValues.length > 0 ? Math.min(...cbdRangeMinValues) : 0;
      const cbdRangeMax = cbdRangeMaxValues.length > 0 ? Math.max(...cbdRangeMaxValues) : 0;
      const thcRangeMin = thcRangeMinValues.length > 0 ? Math.min(...thcRangeMinValues) : 0;
      const thcRangeMax = thcRangeMaxValues.length > 0 ? Math.max(...thcRangeMaxValues) : 0;
      const dominantCannabinoid = getDominant(cannabinoidClassificationValues);
      const potencyUom = getDominant(potencyUomValues);
      const plantType = getDominant(plantTypeValues);

      bundleData.bundleTotalPrice = totalPrice;
      bundleData.bundleTotalDce = totalDce;
      bundleData.averageCbd = averageCbd;
      bundleData.averageThc = averageThc;
      bundleData.cbdRangeMin = cbdRangeMin;
      bundleData.cbdRangeMax = cbdRangeMax;
      bundleData.thcRangeMin = potencyUom && potencyUom == "mg/g" ? parseFloat(thcRangeMin) * 10 : parseFloat(thcRangeMin);
      bundleData.thcRangeMax = potencyUom && potencyUom == "mg/g" ? parseFloat(thcRangeMax) * 10 : parseFloat(thcRangeMax);
      bundleData.potencyUom = potencyUom;
      bundleData.dominantCannabinoid = dominantCannabinoid;
      bundleData.plantType = plantType;
      bundleData.addToShopifyItems = { items: cartShopifyItemsWithRequiredItems };
      bundleData.createCartBundle = cartBundleItems;
      bundleData.bundleData = bundle;
    }

    async createCartBundle(bundleId) {
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Content-Type", "application/json");
      const raw = JSON.stringify({
        token: this.token,
        bundle: parseInt(bundleId),
        bundle_variants: this.productData[bundleId].createCartBundle,
        show_uuid: this.productData[bundleId].bundleData.uuid,
        shop: this.shopDomain,
        user_ip: "",
        click_from_page_type: this.productData[bundleId].bundleData.item_type,
        click_from_url: `/products/${this.productHandle}`,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        redirect: "follow",
        body: raw,
      };

      const apiUrl = "https://api.fastbundle.co/v1/carts/bundles/";
      try {
        const response = await fetch(apiUrl, requestOptions);
        const result = await response.json();
        return result;
      } catch (error) {
        console.error("Error creating bundle cart:", error);
        return null;
      }
    }

    async addItemsToShopify(bundleId) {
      console.log("Adding items to Shopify cart:", this.productData[bundleId].addToShopifyItems);
      try {
        const response = await fetch(`${this.routes.cartAdd}.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.productData[bundleId].addToShopifyItems),
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error adding items to Shopify cart:", errorText);
          throw new Error(`Network response was not ok: ${response.status}`);
        }
        const data = await response.json();
        // dispatch custom 'aurora::addToCart' event
        document.dispatchEvent(
          new CustomEvent("aurora::addToCart", {
            detail: {
              data,
              items: this.productData[bundleId].addToShopifyItems.items,
              response,
              status: response.status,
            },
          })
        );
        return data;
      } catch (error) {
        console.error("Error adding items to Shopify cart:", error);
        throw error;
      }
    }

    renderPrice(bundleId, container = this.container) {
      const priceElement = container.querySelector(".product__price");
      if (!priceElement) {
        console.warn("No price element found");
        return;
      }

      if (!bundleId) {
        console.warn("No bundle ID found");
        return;
      }

      const bundle = this.productData[bundleId];
      priceElement.innerHTML = `$${bundle.bundleTotalPrice.toFixed(2)}`;
    }

    renderDCE(bundleId, container = this.container) {
      const dceElements = container.querySelectorAll(".js--product-dce-value");
      if (dceElements.length === 0) {
        console.warn("No DCE elements found");
        return;
      }
      const bundle = this.productData[bundleId];

      const dceTitle = container.getAttribute("data-dce-title");

      const formattedDce = `${bundle.bundleTotalDce.toFixed(2)}g`;
      const dceString = this.shopLocale != "en" ? `${dceTitle} ${formattedDce}` : `${formattedDce} ${dceTitle}`;

      dceElements.forEach((el) => {
        el.innerHTML = dceString;
      });
    }

    renderThcPotencyGauge(bundleId, container = this.container) {
      const thcPotencyGauge = container.querySelector(".rating-svg-container.thc");
      const textValue = thcPotencyGauge.querySelector(".text-value");
      if (!thcPotencyGauge) {
        console.warn("No THC potency gauge found");
        return;
      }
      const bundle = this.productData[bundleId];
      const averageThc = bundle.averageThc || 0;
      thcPotencyGauge.setAttribute("data-value", averageThc);
      textValue.textContent = `${bundle.thcRangeMin} - ${bundle.thcRangeMax} ${bundle.potencyUom}`
    }

    renderCbdPotencyGauge(bundleId, container = this.container) {
      const cbdPotencyGauge = container.querySelector(".rating-svg-container.cbd");
      const textValue = cbdPotencyGauge.querySelector(".text-value");
      if (!cbdPotencyGauge) {
        console.warn("No CBD potency gauge found");
        return;
      }
      const bundle = this.productData[bundleId];
      const averageCbd = bundle.averageCbd || 0;
      cbdPotencyGauge.setAttribute("data-value", averageCbd);
      textValue.textContent = `${bundle.cbdRangeMin} - ${bundle.cbdRangeMax} ${bundle.potencyUom}`
    }

    renderCannabinoidClassification(bundleId, container = this.container) {
      const classification = this.productData[bundleId]?.dominantCannabinoid;
      if (!classification) {
        console.warn("No cannabinoid classification found");
        return;
      }

      const element = container.querySelector(`.product__cannabis-features-item--${classification}`);
      if (!element) {
        console.warn(`No element found for classification: ${classification}`);
        return;
      }

      element.classList.remove("product__cannabis-features-item-hide");
    }

    renderPlantType(bundleId, container = this.container) {
      let plantType = this.productData[bundleId]?.plantType || "";
      if (!plantType) {
        console.warn("No plant type found");
        return;
      }

      plantType = plantType.replace("plant_type::", "");

      const element = container.querySelector(`.product__cannabis-features-item--type-${plantType}`);
      if (!element) {
        console.warn("No plant type element found");
        return;
      }
      element.classList.remove("product__cannabis-features-item-hide");
    }

    // Cleanup method
    disconnectedCallback() {
      console.log("Bundle component disconnected from DOM");

      // Clean up event listeners
      this.eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this.eventListeners.clear();

      // Clear references
      this.bundleData = [];
      this.productData = {};
      this.token = null;
    }
  }

  window.Bundles = Bundles;
}
